import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  eventTypes,
  pluginList,
  toolbarComponentType,
} from "../../../utils/toolbar";
import Icon from "../../core/Icon";
import FontSelect from "../../core/FontSelect";
import FontSizePicker from "../../core/FontSize";
import ColorPicker from "../../core/ColorPicker";
import AlignmentSelect from "../../core/AlignmentSelect";
import "./style.css";

import useOnClickListener from "../../../hook/useOnClickListener";
import FloatingLinkEditor from "../../core/FloatingLinkEditor";
import FileUploader, { type FileUploaderHandle } from "../../core/FileUploader";
import FilePreview from "../../core/FileUploader/FilePreview";
import { useEmailContext } from "../../GmailWrapper/EmailContext";
import { useGooglePicker } from "../../../hook/useGooglePicker";

// Emoji imports
import EmojiPicker from "./EmojiPicker";
import {
  $createTextNode,
  $getSelection,
  LexicalEditor,
  $isRangeSelection,
} from "lexical";
import { TOGGLE_LINK_COMMAND } from "@lexical/link";
import { INSERT_IMAGE_COMMAND } from "../../../plugins/ImagePlugin/ImagePlugin"; // Added import

interface ToolbarComponentProps {
  onChange: (name: string, value: any) => void;
  state: Record<string, any>;
  setState: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  selectedEventTypes: Record<string, any>;
  blockType: string;
  setAttachments: (files: File[]) => void;
  fileUploaderRef: React.RefObject<FileUploaderHandle | null>;
  setShowEmojiPicker: React.Dispatch<React.SetStateAction<boolean>>;
  setEmojiPosition: React.Dispatch<
    React.SetStateAction<{ top: number; left: number } | null>
  >;
  isLink: boolean;
  editor: LexicalEditor | null;
  openDrivePicker: () => void;
  isMoreVisible: boolean;
  collapsedFormattingPlugins: Record<string, any>;
  visibleFormattingPlugins: Record<string, any>;
  moreOpen: boolean;
  setMoreOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const getToolbarComponent = (
  plugin: any,
  {
    onChange,
    state,
    setState,
    selectedEventTypes,
    blockType,
    setAttachments,
    fileUploaderRef,
    setShowEmojiPicker,
    setEmojiPosition,
    isLink,
    editor,
    openDrivePicker,
  }: ToolbarComponentProps,
) => {
  switch (plugin.name) {
    case eventTypes.undo:
    case eventTypes.redo:
    case eventTypes.bold:
    case eventTypes.italic:
    case eventTypes.underline:
    case eventTypes.indentDecrease:
    case eventTypes.indent:
      // case eventTypes.lockClock:
      // case eventTypes.insertSignature:
      return (
        <button
          key={`${plugin.name}-${plugin.icon}`}
          className={`tool-bar-button ${
            selectedEventTypes?.[plugin.name] ? "active-btn-toolbar" : ""
          }`}
          onClick={() => onChange(plugin.name, true)}
          data-tooltip={plugin.label}
        >
          <Icon className="tool-bar-button-icon" name={plugin.icon} />
        </button>
      );

    case eventTypes.drive:
      return (
        <button
          key={`${plugin.name}-${plugin.icon}`}
          className={`tool-bar-button ${
            selectedEventTypes?.[plugin.name] ? "active-btn-toolbar" : ""
          }`}
          onClick={openDrivePicker}
          data-tooltip="Insert files using Drive"
        >
          <Icon className="tool-bar-button-icon" name={plugin.icon} />
        </button>
      );
    case eventTypes.insertPhoto: // New case for insertPhoto
      return (
        <>
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            id="image-upload"
            onChange={(event) => {
              const files = event.target.files;
              if (files && files.length > 0) {
                const reader = new FileReader();
                reader.onloadend = () => {
                  if (editor) {
                    editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
                      src: reader.result as string,
                      altText: files[0].name,
                    });
                  }
                };
                reader.readAsDataURL(files[0]);
              }
            }}
          />
          <button
            key={`${plugin.name}-${plugin.icon}`}
            className={`tool-bar-button ${
              selectedEventTypes?.[plugin.name] ? "active-btn-toolbar" : ""
            }`}
            onClick={() => {
              document.getElementById("image-upload")?.click();
            }}
            data-tooltip={plugin.label}
          >
            <Icon className="tool-bar-button-icon" name={plugin.icon} />
          </button>
        </>
      );

    case eventTypes.link:
      return (
        <button
          key={`${plugin.name}-${plugin.icon}`}
          className={`tool-bar-button ${isLink ? "active-btn-toolbar" : ""}`}
          onClick={() => onChange(plugin.name, true)}
          data-tooltip={plugin.label}
        >
          <Icon className="tool-bar-button-icon" name={plugin.icon} />
        </button>
      );

    case eventTypes.listNumber:
      return (
        <button
          key={`${plugin.name}-${plugin.icon}`}
          className={`tool-bar-button ${blockType === "ol" ? "active-btn-toolbar" : ""}`}
          onClick={() => onChange(plugin.name, true)}
          data-tooltip={plugin.label}
        >
          <Icon className="tool-bar-button-icon" name={plugin.icon} />
        </button>
      );

    case eventTypes.listBullet:
      return (
        <button
          key={`${plugin.name}-${plugin.icon}`}
          className={`tool-bar-button ${blockType === "ul" ? "active-btn-toolbar" : ""}`}
          onClick={() => onChange(plugin.name, true)}
          data-tooltip={plugin.label}
        >
          <Icon className="tool-bar-button-icon" name={plugin.icon} />
        </button>
      );

    case eventTypes.attachFile:
      return (
        <FileUploader
          ref={fileUploaderRef as React.RefObject<FileUploaderHandle>}
          value={state?.[plugin.name]}
          handleFileChange={(files) => {
            setState((prev) => ({ ...prev, [plugin.name]: files }));
            setAttachments(files);
          }}
        />
      );

    case eventTypes.fontFamily:
      return (
        <FontSelect
          value={selectedEventTypes?.[plugin.name] || state?.[plugin.name]}
          onChange={(e) => onChange(plugin.name, e)}
        />
      );

    case eventTypes.fontSize:
      return (
        <FontSizePicker
          value={selectedEventTypes?.[plugin.name] || state?.[plugin.name]}
          onChange={(e) => onChange(plugin.name, e)}
        />
      );

    case eventTypes.color.base:
      return (
        <ColorPicker
          value={selectedEventTypes?.[plugin.name] || state?.[plugin.name]}
          onChange={(e) => onChange(plugin.name, e)}
        />
      );

    case eventTypes.align.base:
      return (
        <AlignmentSelect
          value={selectedEventTypes?.[plugin.name] || state?.[plugin.name]}
          onChange={(e) => onChange(plugin.name, e)}
        />
      );

    case eventTypes.emoji:
      return (
        <button
          key={plugin.name}
          className="tool-bar-button"
          onMouseDown={(e: any) => {
            e.preventDefault();
            e.stopPropagation();
            console.log(
              "Emoji button clicked, current state:",
              setShowEmojiPicker,
            );
            const rect = e.currentTarget.getBoundingClientRect();
            const topPosition = rect.top + window.scrollY - 440;

            setEmojiPosition({
              top: topPosition < 0 ? rect.bottom + window.scrollY + 10 : topPosition,
              left: Math.max(10, rect.left + window.scrollX),
            });

            setShowEmojiPicker((prev) => !prev);
          }}
          type="button"
          data-tooltip="Insert emoji"
        >
          <div style={{ pointerEvents: "none" }}>
            <Icon className="tool-bar-button-icon" name={plugin.icon} />
          </div>
        </button>
      );

    default:
      break;
  }

  if (plugin.type === toolbarComponentType.divider)
    return <span className="divider" />;
  else return;
};

interface GetComponentProps extends ToolbarComponentProps {
  toggle: boolean;
  setToggle: React.Dispatch<React.SetStateAction<boolean>>;
}

const getComponent = (
  plugin: { type: string; icon?: string; options?: any[]; name?: string },
  props: GetComponentProps & { key: string },
) => {
  const { key, ...componentProps } = props;
  switch (plugin.type) {
    case toolbarComponentType.divider:
      return <div key={key} className="divider" />;

    case toolbarComponentType.component:
      return (
        <div key={key} style={{ display: "contents" }}>
          {getToolbarComponent(plugin, componentProps)}
        </div>
      );

    case toolbarComponentType.styling:
      return (
        <div key={key} className="styling-toolbar">
          {props.toggle && plugin.options?.length ? (
            <div className="styling-options">
              {props.isMoreVisible
                ? props.visibleFormattingPlugins.map(
                    (option: any, index: number) => (
                      <div
                        key={`${option.name}-${index}`}
                        style={{ display: "contents" }}
                      >
                        {!option.hideOnCollapse
                          ? getToolbarComponent(option, componentProps)
                          : null}
                      </div>
                    ),
                  )
                : plugin.options.map((option: any, index: number) => (
                    <div
                      key={`${option.name}-${index}`}
                      style={{ display: "contents" }}
                    >
                      {getToolbarComponent(option, componentProps)}
                    </div>
                  ))}

              {/* More button (visible on small screens via CSS) */}
              <div className="more-wrapper">
                <button
                  className="tool-bar-button more-button"
                  onClick={() => props.setMoreOpen((v) => !v)}
                  data-tooltip="More"
                >
                  <Icon className="tool-bar-button-icon" name="DownArrow" />
                </button>

                {props.moreOpen && (
                  <div className="more-dropdown">
                    {props.collapsedFormattingPlugins.map((option, index) => (
                      <div key={`more-${index}`} className="more-item">
                        <div
                          key={`${option.name}-${index}`}
                          style={{ display: "contents" }}
                        >
                          {getToolbarComponent(option, componentProps)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
          <button
            onClick={() => props.setToggle((prev) => !prev)}
            className="tool-bar-button"
            data-tooltip="Formatting options"
          >
            <Icon className="tool-bar-button-icon" name={plugin.icon as any} />
          </button>
        </div>
      );

    default:
      return;
  }
};

interface ToolBarProps {
  rightChildren?: React.ReactNode;
  leftChildren?: React.ReactNode;
  customTool?: React.ReactNode;
  disabled?: boolean;
  showAttachFile?: boolean;
}

function ToolBar({
  rightChildren,
  leftChildren,
  customTool,
  disabled,
  showAttachFile = true,
}: ToolBarProps) {
  const [toggle, setToggle] = useState(true);
  const [state, setState] = useState<Record<string, any>>({});
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiPosition, setEmojiPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [moreOpen, setMoreOpen] = useState<boolean>(false);
  const [isMoreVisible, setIsMoreVisible] = useState<boolean>(false);
  const stylingPlugin = pluginList.find(
    (p) => p.type === toolbarComponentType.styling,
  );
  const formattingOptions = stylingPlugin?.options || [];

  const collapsedFormattingPlugins = formattingOptions.filter(
    (p: any) => p?.collapse,
  );
  const visibleFormattingPlugins = formattingOptions.filter(
    (p: any) => !p?.collapse,
  );

  const { setAttachments, googleDriveConfig } = useEmailContext();
  const { onChange, isLink, editor, selectedEventTypes, blockType } =
    useOnClickListener();

  const { openPicker } = useGooglePicker(
    googleDriveConfig?.clientId,
    googleDriveConfig?.apiKey,
    (files) => {
      setState((prev) => {
        const currentFiles = prev[eventTypes.attachFile] || [];
        const updatedFiles = [...currentFiles, ...files];
        setAttachments(updatedFiles);
        return { ...prev, [eventTypes.attachFile]: updatedFiles };
      });
    },
    googleDriveConfig?.discoveryDocs,
    googleDriveConfig?.scopes,
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const fileUploaderRef = useRef<FileUploaderHandle>(null);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      setIsMoreVisible(width <= 720);
    });

    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className={`toolbar-container ${disabled ? "toolbar-disabled" : ""}`}
    >
      {state[eventTypes.attachFile]?.length ? (
        <div className="file-upload-container">
          {state[eventTypes.attachFile].map((item: File, index: number) => (
            <FilePreview
              key={item.name + index}
              file={item}
              index={index}
              onRemove={(i) => {
                // Drop it from useFileUpload's own tracked list too — otherwise
                // it reappears the next time a file is added (see FileUploader).
                const uuid = (item as any)?.uuid;
                if (uuid) fileUploaderRef.current?.removeFile(uuid);

                setState((prev: any) => {
                  const updatedFiles = prev[eventTypes.attachFile].filter(
                    (_, idx) => idx !== i,
                  );
                  setAttachments(updatedFiles);
                  return { ...prev, [eventTypes.attachFile]: updatedFiles };
                });
              }}
            />
          ))}
        </div>
      ) : null}

      <div className="toolbar-wrapper">
        {leftChildren}
        <div className="plugin-wrapper">
          {pluginList.map((plugin, index) => {
            if (plugin.name === eventTypes.attachFile && !showAttachFile) {
              return null;
            }
            return getComponent(plugin, {
              key: `${plugin.name || plugin.type}-${index}`,
              toggle,
              setToggle,
              onChange,
              state,
              setState,
              selectedEventTypes,
              blockType,
              setAttachments,
              fileUploaderRef,
              setShowEmojiPicker,
              setEmojiPosition,
              isLink,
              editor,
              openDrivePicker: openPicker,
              isMoreVisible,
              collapsedFormattingPlugins,
              visibleFormattingPlugins,
              moreOpen,
              setMoreOpen,
            });
          })}
          {customTool}
        </div>
        {rightChildren}
        {isLink &&
          createPortal(<FloatingLinkEditor editor={editor} />, document.body)}
      </div>

      {/* Emoji Picker */}
      {showEmojiPicker &&
        emojiPosition &&
        createPortal(
          <div
            className="emoji-picker-container"
            style={{
              position: "absolute",
              top: emojiPosition.top,
              left: emojiPosition.left,
              zIndex: 9999,
            }}
          >
            <EmojiPicker
              onEmojiSelect={(emoji: any) => {
                editor?.update(() => {
                  const selection = $getSelection();
                  if ($isRangeSelection(selection)) {
                    selection.insertNodes([$createTextNode(emoji.native)]);
                  } else if (selection) {
                    selection.insertNodes([$createTextNode(emoji.native)]);
                  }
                });
                setShowEmojiPicker(false);
              }}
            />
          </div>,
          document.body,
        )}
    </div>
  );
}

export default ToolBar;
